import type { ChatMessage, LLMRuntime, ModelFamily } from "./llm-wrapper.js";

// Lazy import. transformers.js is loaded via importmap from CDN at runtime.
let lib: any | null = null;
async function loadLib(): Promise<any> {
  if (lib) return lib;
  lib = await import("@huggingface/transformers");
  return lib;
}

export interface TransformersLoadProgress {
  pct: number;
  text: string;
}

export class TransformersRuntime implements LLMRuntime {
  public readonly family: ModelFamily;
  private inflight: Promise<unknown> | null = null;
  private RawImage: any;

  private constructor(
    private readonly model: any,
    private readonly processor: any,
    RawImage: any,
    family: ModelFamily,
  ) {
    this.family = family;
    this.RawImage = RawImage;
  }

  static async load(
    modelId: string,
    family: ModelFamily,
    onProgress: (p: TransformersLoadProgress) => void,
  ): Promise<TransformersRuntime> {
    const tf = await loadLib();
    const {
      AutoProcessor,
      Gemma4ForConditionalGeneration,
      AutoModelForCausalLM,
      RawImage,
    } = tf;

    onProgress({ pct: 5, text: "Loading processor..." });
    const processor = await AutoProcessor.from_pretrained(modelId);

    onProgress({ pct: 10, text: "Downloading model weights..." });
    const ModelClass =
      family === "gemma4" ? Gemma4ForConditionalGeneration : AutoModelForCausalLM;
    const model = await ModelClass.from_pretrained(modelId, {
      dtype: "q4f16",
      device: "webgpu",
      progress_callback: (info: any) => {
        if (info.status === "progress" || info.status === "progress_total") {
          const raw = typeof info.progress === "number" ? info.progress : 0;
          const pct = Math.min(99, 10 + raw * 0.85);
          const file = info.file ? ` (${info.file})` : "";
          onProgress({ pct, text: `Downloading model${file}...` });
        } else if (info.status === "ready") {
          onProgress({ pct: 99, text: "Initializing model..." });
        }
      },
    });

    onProgress({ pct: 100, text: "Ready" });
    return new TransformersRuntime(model, processor, RawImage, family);
  }

  isBusy(): boolean {
    return this.inflight !== null;
  }

  supportsVision(): boolean {
    return this.family === "gemma4";
  }

  async chat(messages: ChatMessage[], maxTokens: number): Promise<string> {
    return this.runGenerate(messages, null, maxTokens);
  }

  async chatWithImage(
    messages: ChatMessage[],
    image:
      | HTMLImageElement
      | HTMLCanvasElement
      | ImageBitmap
      | ImageData
      | string,
    maxTokens: number,
    stopWords?: string[],
  ): Promise<string> {
    const raw = await this.toRawImage(image);
    if (stopWords && stopWords.length > 0) {
      return this.runGenerateEarlyStop(messages, raw, maxTokens, stopWords);
    }
    return this.runGenerate(messages, raw, maxTokens);
  }

  private async toRawImage(
    image:
      | HTMLImageElement
      | HTMLCanvasElement
      | ImageBitmap
      | ImageData
      | string,
  ): Promise<any> {
    if (typeof image === "string") {
      const tf = await loadLib();
      return tf.load_image(image);
    }
    // RawImage from canvas-like inputs.
    if ((image as HTMLCanvasElement).getContext) {
      return this.RawImage.fromCanvas
        ? this.RawImage.fromCanvas(image as HTMLCanvasElement)
        : await this.RawImage.read(image as HTMLCanvasElement);
    }
    if ((image as HTMLImageElement).naturalWidth !== undefined) {
      return this.RawImage.read(image);
    }
    return this.RawImage.read(image as any);
  }

  private async runGenerate(
    messages: ChatMessage[],
    image: any | null,
    maxTokens: number,
  ): Promise<string> {
    if (this.inflight) {
      try {
        await this.inflight;
      } catch {
        /* ignore — caller already handled */
      }
    }

    const formatted = image
      ? this.injectImageIntoLastUser(messages)
      : messages.map((m) => ({ role: m.role, content: m.content }));

    const prompt = this.processor.apply_chat_template(formatted, {
      add_generation_prompt: true,
      enable_thinking: false,
    });

    const inputs = await this.processor(prompt, image, null, {
      add_special_tokens: false,
    });

    const generation = this.model.generate({
      ...inputs,
      max_new_tokens: maxTokens,
      do_sample: true,
      temperature: 1.0,
      top_k: 64,
      top_p: 0.95,
    });
    this.inflight = generation;
    let outputs: any;
    try {
      outputs = await generation;
    } finally {
      if (this.inflight === generation) this.inflight = null;
    }

    const promptLen = inputs.input_ids.dims.at(-1);
    const decoded = this.processor.batch_decode(
      outputs.slice(null, [promptLen, null]),
      { skip_special_tokens: true },
    );
    return (decoded[0] ?? "").trim();
  }

  private async runGenerateEarlyStop(
    messages: ChatMessage[],
    image: any,
    maxTokens: number,
    stopWords: string[],
  ): Promise<string> {
    if (this.inflight) {
      try {
        await this.inflight;
      } catch {
        /* ignore */
      }
    }
    const tf = await loadLib();
    const { StoppingCriteria } = tf;

    const formatted = this.injectImageIntoLastUser(messages);
    const prompt = this.processor.apply_chat_template(formatted, {
      add_generation_prompt: true,
      enable_thinking: false,
    });
    const inputs = await this.processor(prompt, image, null, {
      add_special_tokens: false,
    });
    const promptLen: number = inputs.input_ids.dims.at(-1);

    const upperStops = stopWords.map((s) => s.toUpperCase());
    const tokenizer = this.processor.tokenizer;
    let matched: string | null = null;

    // Custom stopping criterion: decode the new tokens after each step and
    // halt as soon as one of our stop words appears.
    class WordStop extends StoppingCriteria {
      _call(input_ids: any) {
        if (matched) return new Array(input_ids.length ?? 1).fill(true);
        const seqs: any[] = Array.isArray(input_ids) ? input_ids : input_ids.tolist?.() ?? input_ids;
        const result: boolean[] = [];
        for (const seq of seqs) {
          const arr: number[] = Array.from(seq);
          const newTokens = arr.slice(promptLen);
          if (newTokens.length === 0) {
            result.push(false);
            continue;
          }
          const text = tokenizer
            .decode(newTokens, { skip_special_tokens: true })
            .toUpperCase();
          let hit: string | null = null;
          for (const w of upperStops) {
            if (text.includes(w)) {
              hit = w;
              break;
            }
          }
          if (hit) matched = hit;
          result.push(!!hit);
        }
        return result;
      }
    }

    const stopper = new WordStop();

    const generation = this.model.generate({
      ...inputs,
      max_new_tokens: maxTokens,
      // Greedy decoding pinned the model on a biased token. Sample with a
      // narrow top_k so the model still effectively picks among UP/DOWN/STAY
      // but isn't deterministic-stuck on whichever has the slightly higher logit.
      do_sample: true,
      temperature: 0.7,
      top_k: 3,
      // transformers.js expects an array of StoppingCriteria.
      stopping_criteria: [stopper],
    });
    this.inflight = generation;
    let outputs: any;
    try {
      outputs = await generation;
    } finally {
      if (this.inflight === generation) this.inflight = null;
    }

    if (matched) return matched;
    const decoded = this.processor.batch_decode(
      outputs.slice(null, [promptLen, null]),
      { skip_special_tokens: true },
    );
    return (decoded[0] ?? "").trim();
  }

  private injectImageIntoLastUser(messages: ChatMessage[]): any[] {
    const out: any[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i].role === "user") {
        out[i] = {
          role: "user",
          content: [{ type: "image" }, { type: "text", text: out[i].content }],
        };
        return out;
      }
    }
    out.push({
      role: "user",
      content: [{ type: "image" }, { type: "text", text: "" }],
    });
    return out;
  }
}
